import React, { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { getServices } from '../api/servicesApi';

interface FavoritesContextType {
    favorites: string[] | null;
    toggleFavorite: (id: string) => void;
    refreshFavorites: () => Promise<void>;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export const FavoritesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [favorites, setFavorites] = useState<string[] | null>(null);
    const favoritesRef = React.useRef<string[] | null>(null);
    const LS_KEY = 'favorites';

    const safeRead = () => {
        const stored = localStorage.getItem(LS_KEY);
        if (!stored) return [];
        try {
            const parsed = JSON.parse(stored);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    };

    useEffect(() => {
        const stored = safeRead();
        setFavorites(stored);
        favoritesRef.current = stored;
    }, []);

    useEffect(() => {
        if (favorites !== null) {
            favoritesRef.current = favorites;
            if (favorites.length === 0) {
                localStorage.removeItem(LS_KEY);
            } else {
                localStorage.setItem(LS_KEY, JSON.stringify(favorites));
            }
        }
    }, [favorites]);

    const toggleFavorite = useCallback((id: string) => {
        setFavorites(prev => {
            if (prev === null) return [id];
            if (prev.includes(id)) return prev.filter(item => item !== id);
            return [...prev, id];
        });
    }, []);

    const refreshFavorites = useCallback(async () => {
        const stored = safeRead();
        try {
            const allServices = await getServices();
            const validIds = new Set(allServices.map(s => s.id));
            const filtered = stored.filter(id => validIds.has(id));
            const current = favoritesRef.current ?? [];
            const changed = filtered.length !== current.length || !filtered.every((v, i) => v === current[i]);
            if (changed) setFavorites(filtered);
        } catch {
            setFavorites(stored);
        }
    }, []);

    useEffect(() => {
        const onStorage = (e: StorageEvent) => {
            if (e.key === LS_KEY) refreshFavorites();
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [refreshFavorites]);

    useEffect(() => {
        const onFocus = () => refreshFavorites();
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [refreshFavorites]);

    return (
        <FavoritesContext.Provider value={{ favorites, toggleFavorite, refreshFavorites }}>
            {children}
        </FavoritesContext.Provider>
    );
};

export const useFavorites = () => {
    const context = useContext(FavoritesContext);
    if (!context) throw new Error('useFavorites must be used within a FavoritesProvider');
    return context;
};
